import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Users, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FanPoll - Interactive poll for broadcast pages
 * Home team on LEFT, Away team on RIGHT
 */
export const FanPoll = ({ broadcastId, homeTeam, awayTeam, compact = false }) => {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState(null);

  const fetchPoll = async () => {
    try {
      const response = await axios.get(`${API}/polls/broadcast/${broadcastId}`);
      setPoll(response.data);
    } catch (err) {
      console.error("Error fetching poll:", err);
      setError("Unable to load poll");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (broadcastId) {
      fetchPoll();
    }
  }, [broadcastId]);

  const handleVote = async (team) => {
    if (poll?.has_voted || voting) return;
    
    setVoting(true);
    try {
      const response = await axios.post(`${API}/polls/broadcast/${broadcastId}/vote`, {
        team
      });
      setPoll(response.data);
    } catch (err) {
      console.error("Error voting:", err);
      if (err.response?.data?.detail === "You have already voted") {
        fetchPoll();
      }
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 px-4 py-4 md:py-6">
        <div className="flex items-center justify-center h-12">
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || !poll) {
    return null;
  }

  const home = poll.home_team || homeTeam || {};
  const away = poll.away_team || awayTeam || {};
  const hasVoted = poll.has_voted;

  // Compact mode - matches MatchupBanner height - Home on LEFT, Away on RIGHT
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900/80 border border-white/10 overflow-hidden"
        data-testid="fan-poll"
      >
        <div className="flex items-center px-4 md:px-8 py-4 md:py-6">
          {/* Home Team Vote (Left) */}
          <button
            onClick={() => handleVote("home")}
            disabled={hasVoted || voting}
            className={`flex-1 flex items-center gap-3 md:gap-4 transition-all ${
              hasVoted ? 'cursor-default' : 'hover:bg-white/5 cursor-pointer'
            } ${poll.user_vote === 'home' ? 'bg-green-500/10' : ''} rounded-lg p-2`}
          >
            {home.logo ? (
              <img src={home.logo} alt={home.name} className="w-10 h-10 md:w-12 md:h-12 object-contain" />
            ) : (
              <div 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: home.primary_color || home.color || '#333' }}
              >
                {(home.name || 'H').charAt(0)}
              </div>
            )}
            <div className="text-left flex-1 min-w-0">
              <p className="font-secondary font-bold text-white text-sm md:text-base truncate">
                {home.name || 'Home'}
              </p>
              {hasVoted && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${poll.home_percentage}%` }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: home.primary_color || home.color || '#22c55e' }}
                    />
                  </div>
                  <span className="text-sm font-bold text-white">{poll.home_percentage}%</span>
                </div>
              )}
            </div>
            {poll.user_vote === 'home' && (
              <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
            )}
          </button>

          {/* Center - Poll Info */}
          <div className="flex flex-col items-center px-4 md:px-8 flex-shrink-0">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center border-2 border-white/20">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <span className="text-[8px] md:text-[10px] text-zinc-400 uppercase">Poll</span>
            </div>
            <div className="flex items-center gap-1 mt-1 text-zinc-500 text-[10px]">
              <Users className="w-3 h-3" />
              <span>{poll.total_votes}</span>
            </div>
          </div>

          {/* Away Team Vote (Right) */}
          <button
            onClick={() => handleVote("away")}
            disabled={hasVoted || voting}
            className={`flex-1 flex items-center gap-3 md:gap-4 justify-end transition-all ${
              hasVoted ? 'cursor-default' : 'hover:bg-white/5 cursor-pointer'
            } ${poll.user_vote === 'away' ? 'bg-green-500/10' : ''} rounded-lg p-2`}
          >
            {poll.user_vote === 'away' && (
              <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
            )}
            <div className="text-right flex-1 min-w-0">
              <p className="font-secondary font-bold text-white text-sm md:text-base truncate">
                {away.name || 'Away'}
              </p>
              {hasVoted && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-bold text-white">{poll.away_percentage}%</span>
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${poll.away_percentage}%` }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: away.primary_color || away.color || '#3b82f6' }}
                    />
                  </div>
                </div>
              )}
            </div>
            {away.logo ? (
              <img src={away.logo} alt={away.name} className="w-10 h-10 md:w-12 md:h-12 object-contain" />
            ) : (
              <div 
                className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: away.primary_color || away.color || '#333' }}
              >
                {(away.name || 'A').charAt(0)}
              </div>
            )}
          </button>
        </div>

        {/* Tap to vote hint */}
        {!hasVoted && (
          <div className="bg-black/30 py-1.5 text-center border-t border-white/5">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
              Tap a team to vote
            </span>
          </div>
        )}
      </motion.div>
    );
  }

  // Full mode (original) - Home on LEFT, Away on RIGHT
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="fan-poll"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-3 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <span className="font-secondary text-sm font-bold uppercase tracking-wider text-white">
              Fan Poll
            </span>
          </div>
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <Users className="w-4 h-4" />
            <span>{poll.total_votes} votes</span>
          </div>
        </div>
      </div>

      {/* Poll Content - Home on LEFT, Away on RIGHT */}
      <div className="grid grid-cols-3 gap-0">
        {/* Home Team (Left) */}
        <button
          onClick={() => handleVote("home")}
          disabled={hasVoted || voting}
          className={`relative p-4 transition-all ${
            hasVoted 
              ? 'cursor-default' 
              : 'hover:bg-white/5 cursor-pointer'
          } ${poll.user_vote === 'home' ? 'ring-2 ring-inset ring-green-500' : ''}`}
          style={{ 
            backgroundColor: hasVoted 
              ? `${home.primary_color || '#333'}20` 
              : 'transparent',
            borderRight: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div className="flex flex-col items-center text-center">
            {home.logo ? (
              <img 
                src={home.logo} 
                alt={home.name} 
                className="w-16 h-16 object-contain mb-2"
              />
            ) : (
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl mb-2"
                style={{ backgroundColor: home.primary_color || '#333' }}
              >
                {(home.name || 'H').charAt(0)}
              </div>
            )}
            <span className="font-secondary font-bold text-white text-sm">
              {home.name || 'Home Team'}
            </span>
            {home.mascot && (
              <span className="text-xs text-zinc-500">{home.mascot}</span>
            )}
            
            {poll.user_vote === 'home' && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {hasVoted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4"
            >
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${poll.home_percentage}%` }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: home.primary_color || '#22c55e' }}
                />
              </div>
              <div className="text-center mt-2">
                <span className="font-bold text-2xl text-white">{poll.home_percentage}%</span>
              </div>
            </motion.div>
          )}
        </button>

        {/* Center Question */}
        <div className="flex flex-col items-center justify-center p-4 bg-black/30">
          <span className="text-zinc-400 text-xs uppercase tracking-wider mb-2">
            {hasVoted ? "Results" : "Pick Your Winner"}
          </span>
          <span className="font-secondary text-lg font-bold text-white text-center">
            {hasVoted ? "Thanks for voting!" : "Who will win?"}
          </span>
          {!hasVoted && (
            <span className="text-zinc-500 text-xs mt-2">
              Tap a team to vote
            </span>
          )}
        </div>

        {/* Away Team (Right) */}
        <button
          onClick={() => handleVote("away")}
          disabled={hasVoted || voting}
          className={`relative p-4 transition-all ${
            hasVoted 
              ? 'cursor-default' 
              : 'hover:bg-white/5 cursor-pointer'
          } ${poll.user_vote === 'away' ? 'ring-2 ring-inset ring-green-500' : ''}`}
          style={{ 
            backgroundColor: hasVoted 
              ? `${away.primary_color || '#333'}20` 
              : 'transparent',
            borderLeft: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <div className="flex flex-col items-center text-center">
            {away.logo ? (
              <img 
                src={away.logo} 
                alt={away.name} 
                className="w-16 h-16 object-contain mb-2"
              />
            ) : (
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl mb-2"
                style={{ backgroundColor: away.primary_color || '#333' }}
              >
                {(away.name || 'A').charAt(0)}
              </div>
            )}
            <span className="font-secondary font-bold text-white text-sm">
              {away.name || 'Away Team'}
            </span>
            {away.mascot && (
              <span className="text-xs text-zinc-500">{away.mascot}</span>
            )}
            
            {poll.user_vote === 'away' && (
              <div className="absolute top-2 left-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {hasVoted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4"
            >
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${poll.away_percentage}%` }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: away.primary_color || '#3b82f6' }}
                />
              </div>
              <div className="text-center mt-2">
                <span className="font-bold text-2xl text-white">{poll.away_percentage}%</span>
              </div>
            </motion.div>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default FanPoll;
